//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useLayoutEffect, useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle
} from '@mui/material';

interface SessionTimeoutModalProps {
  open: boolean;
  // Epoch ms at which the session expires; drives the live countdown.
  expiresAt: number;
  // May be async (keepalive / logout); the buttons disable until they settle.
  onStay: () => void | Promise<unknown>;
  onLogout: () => void | Promise<unknown>;
}

const formatRemaining = (ms: number): string => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

// Warns the user that their session is about to expire from inactivity and
// lets them keep it alive.  The countdown ticks once a second only while open;
// the parent (SessionTimeout) owns when this shows and what the actions do.
const SessionTimeoutModal = ({
  open, expiresAt, onStay, onLogout
}: SessionTimeoutModalProps) => {
  // The countdown, advanced once a second while open.  Left at its last value
  // once closing: clicking "Stay" optimistically resets expiresAt to a full
  // TTL, and a live reading would visibly jump back up during the exit
  // transition.
  const [remaining, setRemaining] = useState(0);
  // Disable both buttons while a chosen action is in flight, so a rapid second
  // click can't fire a concurrent keepalive/logout.
  const [pending, setPending] = useState(false);

  // Before paint, so the frame the dialog opens on shows this session's time
  // rather than the previous one's.
  useLayoutEffect(() => {
    if (!open) return;
    const read = () => setRemaining(Math.max(0, expiresAt - Date.now()));
    read();
    const timer = setInterval(read, 1000);
    return () => clearInterval(timer);
  }, [open, expiresAt]);

  const run = (action: () => void | Promise<unknown>) => async () => {
    setPending(true);
    try {
      await action();
    } finally {
      setPending(false);
    }
  };

  return (
    // No onClose: the warning must be answered with an explicit button, so a
    // backdrop click or Escape key does not silently dismiss (and extend) it.
    <Dialog open={open} aria-labelledby="session-timeout-title">
      <DialogTitle id="session-timeout-title">
        Still there?
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {`You'll be signed out in ${formatRemaining(remaining)} due ` +
           'to inactivity. Choose "Stay signed in" to keep working.'}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={run(onLogout)} color="inherit" disabled={pending}>
          Log out
        </Button>
        <Button onClick={run(onStay)} variant="contained" autoFocus
                disabled={pending}>
          Stay signed in
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutModal;
